import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

// "Add a player" modal — handles both existing-user adds and free-text guest adds.
// As the host types a name, we search their network (friends + group co-members).
// If they pick a suggestion, we add the user as a real player (slot reserved,
// they get an email + push). Otherwise we fall through to the existing guest flow.
export default function AddGuestModal({ teeTime, onClose, onAdded }) {
  const [name,  setName]  = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);   // selected user obj
  const [hovered, setHovered] = useState(null); // hovered suggestion id
  const debounceRef = useRef(null);

  const spotsLeft = teeTime.total_spots - teeTime.player_count;
  const invitedCount = teeTime.invited_count || 0;

  // Guest RSVP status chosen at add-time. Defaults to 'invited' (the common
  // "not sure yet" case). 'confirmed' is only selectable when there's an open
  // spot — confirming into a full tee time would 409 server-side anyway.
  const [guestStatus, setGuestStatus] = useState('invited');
  const canConfirmAtAdd = spotsLeft > 0;

  // Debounced network search as the user types (only when no user is picked yet)
  useEffect(() => {
    if (picked) { setSuggestions([]); return; }
    const q = name.trim();
    if (q.length < 2) { setSuggestions([]); return; }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await api.searchNetwork(q, teeTime.id);
        setSuggestions(results || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 200);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [name, picked, teeTime.id]);

  const pickUser = (u) => {
    setPicked(u);
    setName(u.name);
    setSuggestions([]);
    setError('');
  };

  const clearPicked = () => {
    setPicked(null);
    setName('');
    setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    // Existing-user path
    if (picked) {
      setLoading(true);
      try {
        await api.addPlayer(teeTime.id, picked.id);
        onAdded({ id: picked.id, name: picked.name, type: 'player' });
        onClose();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Guest path
    if (!name.trim()) { setError('Name is required.'); return; }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const guest = await api.addGuest(teeTime.id, {
        name:  name.trim(),
        email: email.trim() || undefined,
        status: guestStatus,
      });
      onAdded(guest);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // An existing-user pick is added as a confirmed player (slot reserved).
  // A free-text guest is added with the status the host chose in the picker.
  const submitLabel = loading
    ? (picked ? 'Adding…' : 'Adding…')
    : picked
      ? 'Add to tee time'
      : guestStatus === 'confirmed'
        ? (email.trim() ? 'Add confirmed & send invite' : 'Add as confirmed')
        : (email.trim() ? 'Add invited & send invite' : 'Add as invited');

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>Add a player</h3>

        {/* Context banner */}
        <div style={{
          background: '#f4f1eb', borderRadius: 9,
          padding: '0.75rem', marginBottom: '1rem',
        }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#0d2b1a' }}>
            {teeTime.course_name}
          </div>
          <div style={{ fontSize: 12, color: '#7a7a6a', marginTop: 2 }}>
            {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} remaining
            {invitedCount > 0 && ` · ${invitedCount} invited (not yet confirmed)`}
          </div>
        </div>

        <form onSubmit={submit}>
          {/* Name with smart suggestions */}
          <div className="form-group" style={{ position: 'relative' }}>
            <label className="form-label">
              Player name <span style={{ color: '#c45a3a' }}>*</span>
            </label>

            {picked ? (
              // "Picked user" pill display — read-only, with an X to clear
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: '#e8f5ec', border: '1px solid #7ec99a',
                borderRadius: 8, padding: '8px 10px',
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: '#7ec99a', color: '#0d2b1a',
                  fontSize: 11, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>{picked.initials}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#0d2b1a' }}>{picked.name}</div>
                  <div style={{ fontSize: 11, color: '#5a5a4a' }}>
                    {picked.is_friend && <span>Friend</span>}
                    {picked.is_friend && picked.mutual_groups?.length > 0 && <span> · </span>}
                    {picked.mutual_groups?.length > 0 && (
                      <span>{picked.mutual_groups.slice(0, 2).join(', ')}{picked.mutual_groups.length > 2 ? ` +${picked.mutual_groups.length - 2}` : ''}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearPicked}
                  aria-label="Clear selection"
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    fontSize: 18, color: '#5a5a4a', padding: '0 4px',
                  }}
                >×</button>
              </div>
            ) : (
              <input
                className="form-input"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Start typing a name…"
                autoFocus
                autoComplete="off"
              />
            )}

            {/* Suggestion dropdown */}
            {!picked && suggestions.length > 0 && (
              <div style={{
                marginTop: 4, background: '#fff',
                border: '1px solid #d8d4c4', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                maxHeight: 240, overflowY: 'auto',
              }}>
                {suggestions.map(u => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => pickUser(u)}
                    onMouseEnter={() => setHovered(u.id)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '8px 10px',
                      background: hovered === u.id ? '#f4f1eb' : '#fff',
                      border: 'none', borderBottom: '0.5px solid #f0ede4',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: '#d8e6d4', color: '#0d2b1a',
                      fontSize: 11, fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>{u.initials}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#0d2b1a', fontWeight: 500 }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: '#7a7a6a' }}>
                        {u.is_friend && <span>Friend</span>}
                        {u.is_friend && u.mutual_groups?.length > 0 && <span> · </span>}
                        {u.mutual_groups?.length > 0 && (
                          <span>{u.mutual_groups.slice(0, 2).join(', ')}{u.mutual_groups.length > 2 ? ` +${u.mutual_groups.length - 2}` : ''}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Hint text */}
            {!picked && (
              <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 4, lineHeight: 1.5 }}>
                {searching
                  ? 'Searching…'
                  : name.trim().length >= 2 && suggestions.length === 0
                    ? 'No matches in your network — they\'ll be added as a guest (status: invited).'
                    : 'Type a name to find friends or group members, or add anyone as a guest.'}
              </div>
            )}
          </div>

          {/* Email — guest path only */}
          {!picked && (
            <div className="form-group">
              <label className="form-label">
                Email address{' '}
                <span style={{ color: '#9a9a8a', fontWeight: 400,
                               textTransform: 'none', letterSpacing: 0 }}>
                  (optional)
                </span>
              </label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="dave@email.com"
              />
              <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 4, lineHeight: 1.5 }}>
                {email.trim()
                  ? `We'll send ${name.trim() || 'them'} the tee time details and a link to download Greentime.`
                  : 'Add an email to notify them about the tee time and invite them to download Greentime.'}
              </div>
            </div>
          )}

          {/* Guest RSVP status picker — guest path only. Lets the host set
              status at add-time instead of adding then confirming separately. */}
          {!picked && (
            <div className="form-group">
              <label className="form-label">Status</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { key: 'invited',   label: 'Invited',   sub: "Doesn't take a spot yet" },
                  { key: 'confirmed', label: 'Confirmed', sub: 'Takes a spot now' },
                ].map(opt => {
                  const active   = guestStatus === opt.key;
                  const disabled = opt.key === 'confirmed' && !canConfirmAtAdd;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      disabled={disabled}
                      onClick={() => setGuestStatus(opt.key)}
                      style={{
                        flex: 1, textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
                        padding: '8px 10px', borderRadius: 8,
                        border: active ? '1.5px solid #2e7d4f' : '1.5px solid #d8d4c4',
                        background: disabled ? '#f4f1eb' : active ? '#e8f5ec' : '#fff',
                        opacity: disabled ? 0.55 : 1,
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 500,
                                    color: active ? '#1a5c32' : '#0d2b1a' }}>
                        {opt.label}
                      </div>
                      <div style={{ fontSize: 10, color: '#7a7a6a', marginTop: 1 }}>
                        {disabled ? 'Tee time is full' : opt.sub}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 5, lineHeight: 1.5 }}>
                You can change a guest's status anytime from the player list.
              </div>
            </div>
          )}

          {error && <p className="form-error">{error}</p>}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-accent btn-sm"
              disabled={loading}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
